import { routes } from '@app/app.routes';

import { Component, OnInit, computed, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { ToastModule } from 'primeng/toast';

import { NgIcon } from '@ng-icons/core';

import { CustomToast } from '@components/custom-toast/custom-toast.component';
import { InactivityWarningComponent } from '@components/inactivity-warning/inactivity-warning.component';

import { MODULES } from '@configs/modules.enum';

import {
  ErrorStore,
  LoaderState,
  RouterUtilsService,
  UserStore,
} from '@cxc/core/services';
import { SessionInactivityService } from '@services/DOM/session-inactivity.service';
import { SessionService } from '@services/session/session.service';
import { CustomBreadcrumbState } from '@services/state/custom-breadcrum.state';
import { FacilitiesCache } from '@services/state/facilities-cache/facilities-cache.service';
import { OperationsState } from '@services/state/operation-state/operation.state';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    InactivityWarningComponent,
    ToastModule,
    NgIcon,
    CustomToast,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [],
})
export class AppComponent implements OnInit {
  title = 'cxc';

  operationState = inject(OperationsState);
  loaderState = inject(LoaderState);
  errorState = inject(ErrorStore);
  customBreadcrumbState = inject(CustomBreadcrumbState);
  facilitiesCache = inject(FacilitiesCache);

  userStore = inject(UserStore);
  inactivityService = inject(SessionInactivityService);
  private session = inject(SessionService);
  routerUtils = inject(RouterUtilsService);

  constructor() {
    // Public API for the remotes (AIVA calls it on a 401). LEN-2764: revokes
    // the session and tells the other tabs, like every other logout path.
    (window as any).logout = () => this.session.logout();

    this.routerUtils.registerRoutes(routes, MODULES.PORTFOLIO);

    this.routerUtils.registerRouteDataKey({
      key: '{operation.alias}',
      replacementFn: computed(() => {
        if (!this.operationState.activeOperation()) {
          return '• • •';
        }
        return this.operationState.activeOperation().alias;
      }),
      module: MODULES.PORTFOLIO,
    });

    // Called by the companies remote after it creates a facility (LEN-2587).
    (window as any).invalidateFacilities = () =>
      this.facilitiesCache.invalidate();

    (window as any).loaderState = this.loaderState;
    (window as any).errorState = this.errorState;

    (window as any).registerRoutes = this.routerUtils.registerRoutes.bind(
      this.routerUtils
    );
    (window as any).registerRouteDataKey =
      this.routerUtils.registerRouteDataKey.bind(this.routerUtils);

    effect(() => {
      const user = this.userStore.signal();

      if (user?.logged) {
        this.inactivityService.start();
      } else {
        this.inactivityService.stop();
      }
    });
  }

  ngOnInit() {
    (window as any).showCustomBreadcrumbs = (items: any[]) =>
      this.customBreadcrumbState.show(items);

    (window as any).removeCustomBreadcrumbs = () =>
      this.customBreadcrumbState.clear();
  }
}
